<template>
  <div>
    <v-card class="mb-4">
      <v-card-text class="d-flex align-center flex-wrap">
        <div class="me-6">
          <div class="text-caption text--secondary">
            Активная версия
          </div>
          <div class="text-h5">
            {{ actual ? actual.version : '—' }}
          </div>
        </div>

        <v-divider
          vertical
          class="me-6"
        ></v-divider>

        <div class="me-6">
          <div class="text-caption text--secondary">
            Синхронизировано
          </div>
          <div class="text-subtitle-1">
            {{ actual ? toDateTimeString(actual.synchronizationTime) : 'ещё ни разу' }}
          </div>
        </div>

        <v-spacer></v-spacer>

        <v-btn
          color="primary"
          :loading="syncing"
          @click="sync()"
        >
          <v-icon
            size="18"
            class="me-1"
          >
            {{ icons.mdiSync }}
          </v-icon>
          <span>Синхронизировать</span>
        </v-btn>
      </v-card-text>

      <v-card-text
        v-if="actual"
        class="pt-0"
      >
        <v-btn
          color="primary"
          outlined
          :to="{ name: 'menu-version-menu-category-list', params: { menuVersionId: actual.id } }"
        >
          <v-icon
            size="18"
            class="me-1"
          >
            {{ icons.mdiFormatListBulleted }}
          </v-icon>
          <span>Открыть категории и блюда</span>
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card id="promotion-list">
      <v-card-text class="d-flex align-center flex-wrap pb-0">
        <div class="text-h6 pb-5">
          Версии меню
        </div>
        <v-spacer></v-spacer>
        <div class="d-flex align-center pb-5">
          <v-text-field
            v-model="filters.searchQuery"
            single-line
            dense
            outlined
            hide-details
            placeholder="Поиск..."
            class="promotion-list-search me-3"
          ></v-text-field>
        </div>
      </v-card-text>

    <v-data-table
      :headers="headers"
      :items="menuVersions"
      :server-items-length="totalCount"
      :loading="loading"
      :page.sync="filters.pageNumber"
      :items-per-page.sync="filters.pageSize"
      :sort-by.sync="filters.sortBy"
      :sort-desc.sync="filters.sortDesc"
      class="text-no-wrap"
    >
      <template #[`item.version`]="{item}">
        {{ item.version }}
      </template>

      <template #[`item.synchronizationTime`]="{item}">
        {{ toDateTimeString(item.synchronizationTime) }}
      </template>

      <template #[`item.isActual`]="{item}">
        <v-chip
          small
          :color="item.isActual ? 'success' : undefined"
          :outlined="!item.isActual"
        >
          {{ item.isActual ? 'Активна' : 'Архив' }}
        </v-chip>
      </template>

      <template #[`item.actions`]="{item}">
        <div class="d-flex align-center justify-center">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                :to="{ name: 'menu-version-menu-category-list', params: { menuVersionId: item.id } }"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiEye }}
                </v-icon>
              </v-btn>
            </template>
            <span>Просмотреть</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                :disabled="item.isActual || hasNotPermission('Permissions.MenuVersionActivate')"
                v-bind="attrs"
                @click="activateMenu(item.id)"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiCheck }}
                </v-icon>
              </v-btn>
            </template>
            <span>Активировать</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                :to="{ name: 'menu-version-translations', params: { id: item.id } }"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiFlag }}
                </v-icon>
              </v-btn>
            </template>
            <span>Конфигурация языков</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  mdiTrendingUp,
  mdiPlus,
  mdiCheck,
  mdiDeleteOutline,
  mdiDotsVertical,
  mdiEyeOutline,
  mdiFlag,
  mdiCheckCircleOutline,
  mdiAlertCircleOutline,
  mdiPencilOutline,
  mdiEye,
  mdiSync,
  mdiFormatListBulleted,
} from '@mdi/js'
import { mapFormHandlers } from '@/helpers/forms'
import { toDateTimeString, hasPermission } from '@/helpers'

export default {
  name: 'MenuVersionList',
  data: () => ({
    toDateTimeString,
    icons: {
      mdiCheckCircleOutline,
      mdiTrendingUp,
      mdiPlus,
      mdiAlertCircleOutline,
      mdiPencilOutline,
      mdiDeleteOutline,
      mdiDotsVertical,
      mdiCheck,
      mdiFlag,
      mdiEyeOutline,
      mdiEye,
      mdiSync,
      mdiFormatListBulleted,
    },
    syncing: false,
  }),
  computed: {
    ...mapFormHandlers('menuVersion', [
      'filters.pageNumber',
      'filters.pageSize',
      'filters.searchQuery',
      'filters.sortBy',
      'filters.sortDesc',
    ]),
    ...mapState('menuVersion', {
      menuVersions: 'list',
      totalCount: 'totalCount',
      loading: 'loading',
    }),
    actual() {
      return this.menuVersions.find(v => v.isActual)
    },
    headers() {
      return [
        { text: 'Версия', value: 'version' },
        { text: 'Дата', value: 'synchronizationTime' },
        { text: 'Активное', value: 'isActual' },
        {
          text: 'Действия',
          value: 'actions',
          align: 'center',
          sortable: false,
        },
      ]
    },
  },
  async mounted() {
    await this.fetchMenuVersions()
  },
  methods: {
    hasNotPermission(value) {
      return !hasPermission(value)
    },
    async sync() {
      this.syncing = true
      try {
        await this.syncMenu()
        await this.fetchMenuVersions()
      } finally {
        this.syncing = false
      }
    },
    ...mapActions('menuVersion', {
      fetchMenuVersions: 'fetchList',
      syncMenu: 'sync',
      activate: 'updateVisibility',
    }),
    resolveMenuStatusVariantAndIcon(status) {
      if (status) return { variant: 'success', icon: mdiCheckCircleOutline }

return { variant: 'error', icon: mdiAlertCircleOutline }
    },
    async activateMenu(id) {
      await this.activate({
        menuVersionId: id,
      })
      await this.fetchMenuVersions()
    },
  },
}
</script>

<style lang="scss" scoped>
#promotion-list {
  .promotion-list-search {
    max-width: 10.625rem;
  }
}
</style>
